export const ensure = (key, value) => {
  if (value === undefined || value === null) {
    throw new Error(`argument '${key}' is required and was missing`);
  }
};

// matches a bare parameter identifier (no default, destructuring, or rest)
const IDENTIFIER = /^[A-Za-z_$][\w$]*$/;

/**
 * Extracts a function's parameter names from its source (via `func.toString()`),
 * using a lightweight scan rather than a full JS parser.
 *
 * Only bare identifier params yield a name; defaults (`a = 1`), destructured
 * (`{ x }`, `[ y ]`), and rest (`...r`) params yield `undefined` — matching the
 * behaviour of the previous AST-based implementation, whose consumer
 * (`ensureArguments`) filters those out before validating.
 *
 * Caveat: the scan is not string/comment aware. A default value containing a
 * top-level comma or unbalanced bracket inside a string/regex literal or a
 * comment in the signature (e.g. `a = "x,y"`) will mis-split the param list —
 * a divergence from the old AST parser. Safe for bare-identifier / simple
 * signatures only; the sole caller (`rotatePoint`) uses bare identifiers.
 *
 * @see {@link https://stackoverflow.com/questions/1007981/how-to-get-function-parameter-names-values-dynamically}
 * @param {function} func - the function to parse argument keys for
 * @returns {(string|undefined)[]} the parameter names in declaration order
 */
export const parseFunctionArguments = func => {
  const source = func.toString().trim();

  // single-argument arrow without parens, e.g. `arg => ...`
  const noParenArrow = source.match(/^(?:async\s+)?([A-Za-z_$][\w$]*)\s*=>/);
  if (noParenArrow) return [noParenArrow[1]];

  // locate the parameter list: the first `(` and its matching `)`
  const open = source.indexOf("(");
  if (open === -1) return [];

  let depth = 0;
  let close = -1;
  for (let i = open; i < source.length; i++) {
    const char = source[i];
    if (char === "(") depth++;
    else if (char === ")") {
      depth--;
      if (depth === 0) {
        close = i;
        break;
      }
    }
  }
  if (close === -1) return [];

  const paramList = source.slice(open + 1, close).trim();
  if (paramList === "") return [];

  // split on top-level commas, respecting nested (), {}, [] in default values
  const params = [];
  let current = "";
  depth = 0;
  for (const char of paramList) {
    if (char === "(" || char === "{" || char === "[") depth++;
    else if (char === ")" || char === "}" || char === "]") depth--;

    if (char === "," && depth === 0) {
      params.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  params.push(current);

  return params.map(param => {
    const name = param.trim();
    return IDENTIFIER.test(name) ? name : undefined;
  });
};

/**
 * @desc ensures values for the given function arguments are not undefined or null
 * @param {function|string[]} funcOrArgumentKeys - the function the arguments are for,
 * or explicit argument keys when production minification would rename params
 * @param {[]} args - arguments provided
 * @param {function} [filter] - an optional filter for argument keys
 */
export const ensureArguments = (funcOrArgumentKeys, args, filter = x => x) => {
  // get the argument keys for the function
  const argumentKeys = Array.isArray(funcOrArgumentKeys)
    ? funcOrArgumentKeys
    : parseFunctionArguments(funcOrArgumentKeys);

  // map key-value for arguments
  const argsMap = {};
  argumentKeys.forEach((argKey, index) => {
    argsMap[argKey] = args[index];
  });

  // apply filter function to keys
  const filteredArgKeys = argumentKeys.filter(filter);

  // ensure all expected arguments have values
  filteredArgKeys.forEach(argKey => ensure(argKey, argsMap[argKey]));
};
